import type { z } from 'zod';
import {
  INTERCEPT_TOGGLE_TOOL_NAME,
  INTERCEPT_RULE_TOOL_NAME,
  LAUNCH_BROWSER_TOOL_NAME,
  MATCH_REPLACE_RULE_TOOL_NAME,
  PORTAL_TOOL_NAMES,
  PROXY_CONTROL_TOOL_NAME,
  PROXY_HISTORY_TOOL_NAME,
  REPLAY_REQUEST_TOOL_NAME,
  BROWSER_AUTOMATION_TOOL_NAME,
  MCP_CREDENTIAL_DIALOG_TOOL_NAME,
  MCP_OAUTH_AUTHORIZE_TOOL_NAME,
  TRACK_HISTORY_REQUEST_TOOL_NAME,
  type PortalToolName,
} from './portal-tool.constants';
import type { PortalToolOutputBase } from './portal-tool.schemas';
import { InterceptToggleOutputSchema } from './intercept-toggle.schema';
import { InterceptRuleOutputSchema } from './intercept-rule.schema';
import { LaunchBrowserOutputSchema } from './launch-browser.schema';
import { MatchReplaceRuleOutputSchema } from './match-replace-rule.schema';
import { ProxyControlOutputSchema } from './proxy-control.schema';
import { ProxyHistoryOutputSchema } from './proxy-history.schema';
import { BrowserAutomationOutputSchema } from './browser-automation.schema';
import { McpCredentialDialogOutputSchema } from './mcp-credential-dialog.schema';
import { McpOAuthAuthorizeOutputSchema } from './mcp-oauth-authorize.schema';
import {
  ReplayRequestOutputSchema,
  type ReplayRequestOutput,
} from './replay-request.schema';
import {
  TrackHistoryRequestOutputSchema,
  type TrackHistoryRequestOutput,
} from './track-history-request.schema';
import type { InterceptToggleOutput } from './intercept-toggle.schema';
import type { InterceptRuleOutput } from './intercept-rule.schema';
import type { LaunchBrowserOutput } from './launch-browser.schema';
import type { MatchReplaceRuleOutput } from './match-replace-rule.schema';
import type { ProxyControlOutput } from './proxy-control.schema';
import type { ProxyHistoryOutput } from './proxy-history.schema';
import type { BrowserAutomationOutput } from './browser-automation.schema';
import type { McpCredentialDialogOutput } from './mcp-credential-dialog.schema';
import type { McpOAuthAuthorizeOutput } from './mcp-oauth-authorize.schema';

export type PortalToolOutput =
  | ProxyControlOutput
  | InterceptToggleOutput
  | InterceptRuleOutput
  | MatchReplaceRuleOutput
  | ReplayRequestOutput
  | LaunchBrowserOutput
  | ProxyHistoryOutput
  | BrowserAutomationOutput
  | TrackHistoryRequestOutput
  | McpOAuthAuthorizeOutput
  | McpCredentialDialogOutput;

const PORTAL_TOOL_OUTPUT_SCHEMAS = {
  [PROXY_CONTROL_TOOL_NAME]: ProxyControlOutputSchema,
  [INTERCEPT_TOGGLE_TOOL_NAME]: InterceptToggleOutputSchema,
  [INTERCEPT_RULE_TOOL_NAME]: InterceptRuleOutputSchema,
  [MATCH_REPLACE_RULE_TOOL_NAME]: MatchReplaceRuleOutputSchema,
  [REPLAY_REQUEST_TOOL_NAME]: ReplayRequestOutputSchema,
  [LAUNCH_BROWSER_TOOL_NAME]: LaunchBrowserOutputSchema,
  [PROXY_HISTORY_TOOL_NAME]: ProxyHistoryOutputSchema,
  [BROWSER_AUTOMATION_TOOL_NAME]: BrowserAutomationOutputSchema,
  [TRACK_HISTORY_REQUEST_TOOL_NAME]: TrackHistoryRequestOutputSchema,
  [MCP_OAUTH_AUTHORIZE_TOOL_NAME]: McpOAuthAuthorizeOutputSchema,
  [MCP_CREDENTIAL_DIALOG_TOOL_NAME]: McpCredentialDialogOutputSchema,
} satisfies Record<PortalToolName, z.ZodType<PortalToolOutputBase>>;

export function getPortalToolOutputSchema(
  toolName: string,
): z.ZodType<PortalToolOutputBase> | undefined {
  if (!isRegisteredPortalToolName(toolName)) {
    return undefined;
  }

  return PORTAL_TOOL_OUTPUT_SCHEMAS[toolName];
}

export function isRegisteredPortalToolName(
  toolName: string,
): toolName is PortalToolName {
  return (PORTAL_TOOL_NAMES as readonly string[]).includes(toolName);
}
